// Snip from plans — open one of the job's Fieldwire sheets (or one of the crews' pin
// photos), zoom to the problem, ring it or point an arrow at it, and capture exactly
// what's on screen as a PNG; the RFI attaches it. Capture as many views as you need —
// the window stays open, the count climbs, Done closes it. Images come through the API
// (same origin as the token) so the canvas isn't tainted — Fieldwire's file host sends
// no CORS headers.
import React, { useEffect, useRef, useState } from 'react';
import { apiFetch, API_BASE, getToken } from '../../services/apiClient';

const NAVY = '#1e3a5f', ORANGE = '#e87722', RED = '#dc2626';
const lbl = { fontSize: 10, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 };
const muted = { fontSize: 13, color: '#9ca3af' };
const btn = { padding: '7px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' };
const fmtWhen = s => { try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); } catch { return ''; } };

export default function PlanSnip({ projectId, title = 'Snip from the plans', onCapture, onClose }) {
  const [tab, setTab] = useState('sheets');       // sheets | photos
  const [sheets, setSheets] = useState(null);
  const [photos, setPhotos] = useState(null);
  const [src, setSrc] = useState(null);           // { kind, id, name, url } — what's in the viewer
  const [imgUrl, setImgUrl] = useState(null);
  const [img, setImg] = useState(null);           // the loaded HTMLImageElement
  const [view, setView] = useState({ scale: 1, tx: 0, ty: 0 });
  const [mode, setMode] = useState('pan');        // pan | ring | arrow
  const [marks, setMarks] = useState([]);         // { type:'ring', x, y } | { type:'arrow', x1, y1, x2, y2 } — image pixels
  const [pending, setPending] = useState(null);   // the arrow being dragged out
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [captured, setCaptured] = useState([]);   // names of what's been attached this session
  const [flash, setFlash] = useState('');
  const boxRef = useRef(null);
  const drag = useRef(null);
  // The arrow being dragged lives in a ref as well as state, so down/move/up landing in one
  // tick (fast mice, touch, scripted events) still build the arrow — state alone lags a render.
  const pendingRef = useRef(null);
  const setPend = a => { pendingRef.current = a; setPending(a); };

  useEffect(() => {
    apiFetch(`/projects/${projectId}/fieldwire/sheets`).then(s => setSheets(Array.isArray(s) ? s : [])).catch(() => setSheets([]));
    apiFetch(`/projects/${projectId}/fieldwire/photos`).then(s => setPhotos(Array.isArray(s) ? s : [])).catch(() => setPhotos([]));
  }, [projectId]);

  // Pick a sheet or photo: pull the image as a blob and fit it to the window.
  useEffect(() => {
    if (!src) return undefined;
    let live = true, url = null;
    setBusy('img'); setErr(''); setImg(null); setMarks([]); pendingRef.current = null; setPending(null);
    fetch(`${API_BASE}${src.url}`, { headers: { 'x-worker-token': getToken() } })
      .then(r => { if (!r.ok) throw new Error('load'); return r.blob(); })
      .then(b => {
        if (!live) return;
        url = URL.createObjectURL(b); setImgUrl(url);
        const im = new Image();
        im.onload = () => {
          if (!live) return;
          const box = boxRef.current?.getBoundingClientRect();
          const s = box ? Math.min(box.width / im.naturalWidth, box.height / im.naturalHeight) : 0.3;
          setView({ scale: s, tx: box ? (box.width - im.naturalWidth * s) / 2 : 0, ty: box ? (box.height - im.naturalHeight * s) / 2 : 0 });
          setImg(im); setBusy('');
        };
        im.onerror = () => { if (live) { setErr('That image would not load.'); setBusy(''); } };
        im.src = url;
      })
      .catch(() => { if (live) { setErr('That image would not load.'); setBusy(''); } });
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [src]);

  // The wheel listener is attached by hand because React registers onWheel passively,
  // and a passive listener can't stop the page behind the window from scrolling.
  useEffect(() => {
    const el = boxRef.current; if (!el) return undefined;
    const onWheel = e => {
      if (!img) return;
      e.preventDefault();
      const box = el.getBoundingClientRect();
      const px = e.clientX - box.left, py = e.clientY - box.top;
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      setView(v => {
        const scale = Math.min(8, Math.max(0.05, v.scale * factor));
        const k = scale / v.scale;
        return { scale, tx: px - (px - v.tx) * k, ty: py - (py - v.ty) * k };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [img]);

  const toImage = (clientX, clientY) => {
    const box = boxRef.current.getBoundingClientRect();
    return { x: (clientX - box.left - view.tx) / view.scale, y: (clientY - box.top - view.ty) / view.scale };
  };
  function onDown(e) {
    if (!img) return;
    if (mode === 'ring') { const p = toImage(e.clientX, e.clientY); setMarks(m => [...m, { type: 'ring', x: p.x, y: p.y }]); return; }
    if (mode === 'arrow') { const p = toImage(e.clientX, e.clientY); setPend({ type: 'arrow', x1: p.x, y1: p.y, x2: p.x, y2: p.y }); return; }
    drag.current = { x: e.clientX, y: e.clientY, tx: view.tx, ty: view.ty };
  }
  function onMove(e) {
    if (pendingRef.current) { const p = toImage(e.clientX, e.clientY); setPend({ ...pendingRef.current, x2: p.x, y2: p.y }); return; }
    if (!drag.current) return;
    const d = drag.current;
    setView(v => ({ ...v, tx: d.tx + (e.clientX - d.x), ty: d.ty + (e.clientY - d.y) }));
  }
  function onUp() {
    const a = pendingRef.current;
    if (a) {
      const len = Math.hypot(a.x2 - a.x1, a.y2 - a.y1) * view.scale;
      if (len >= 12) setMarks(m => [...m, a]);   // a click without a drag is not an arrow
      setPend(null);
    }
    drag.current = null;
  }
  function fit() {
    if (!img) return;
    const box = boxRef.current.getBoundingClientRect();
    const s = Math.min(box.width / img.naturalWidth, box.height / img.naturalHeight);
    setView({ scale: s, tx: (box.width - img.naturalWidth * s) / 2, ty: (box.height - img.naturalHeight * s) / 2 });
  }
  // Mark sizes: a fixed share of the window, so they read the same however far in you are.
  const ringPx = () => Math.max(14, Math.min(boxRef.current?.clientWidth || 800, boxRef.current?.clientHeight || 600) * 0.045);
  const strokePx = () => Math.max(3, ringPx() / 6);

  // Exactly the window's view, at up to source resolution, marks drawn on. Stays open.
  async function capture() {
    if (!img) return;
    setBusy('cap'); setErr('');
    try {
      const box = boxRef.current.getBoundingClientRect();
      let x0 = -view.tx / view.scale, y0 = -view.ty / view.scale, w = box.width / view.scale, h = box.height / view.scale;
      // Clip to the image so a zoomed-out view doesn't capture grey margins.
      const x1 = Math.min(img.naturalWidth, x0 + w), y1 = Math.min(img.naturalHeight, y0 + h);
      x0 = Math.max(0, x0); y0 = Math.max(0, y0); w = x1 - x0; h = y1 - y0;
      if (w < 10 || h < 10) throw new Error('empty');
      const f = Math.min(1, 2400 / w, 2400 / h);        // source resolution, capped at 2400px a side
      const c = document.createElement('canvas');
      c.width = Math.round(w * f); c.height = Math.round(h * f);
      const g = c.getContext('2d');
      g.fillStyle = '#fff'; g.fillRect(0, 0, c.width, c.height);
      g.drawImage(img, x0, y0, w, h, 0, 0, c.width, c.height);
      const r = (ringPx() / view.scale) * f, lw = (strokePx() / view.scale) * f;
      g.strokeStyle = RED; g.fillStyle = RED; g.lineWidth = lw; g.lineCap = 'round'; g.lineJoin = 'round';
      const P = (x, y) => [(x - x0) * f, (y - y0) * f];
      for (const m of marks) {
        if (m.type === 'ring') { const [cx, cy] = P(m.x, m.y); g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.stroke(); }
        else {
          const [ax, ay] = P(m.x1, m.y1), [bx, by] = P(m.x2, m.y2);
          const ang = Math.atan2(by - ay, bx - ax), head = Math.max(lw * 4, r * 0.9);
          // Shaft stops short of the tip so the head's point is the exact spot.
          g.beginPath(); g.moveTo(ax, ay); g.lineTo(bx - Math.cos(ang) * head * 0.7, by - Math.sin(ang) * head * 0.7); g.stroke();
          g.beginPath(); g.moveTo(bx, by);
          g.lineTo(bx - head * Math.cos(ang - Math.PI / 7), by - head * Math.sin(ang - Math.PI / 7));
          g.lineTo(bx - head * Math.cos(ang + Math.PI / 7), by - head * Math.sin(ang + Math.PI / 7));
          g.closePath(); g.fill();
        }
      }
      const blob = await new Promise(res => c.toBlob(res, 'image/png'));
      if (!blob) throw new Error('blob');
      const safe = String(src.name || 'image').replace(/[^A-Za-z0-9._-]+/g, '_').slice(0, 40);
      const n = captured.length + 1;
      const name = `${safe}-${src.kind === 'photo' ? 'photo' : 'snip'}${n > 1 ? '-' + n : ''}.png`;
      // The spot this snip is about, in the sheet's own pixels: the ring, else the
      // arrow's tip, else the middle of the view. Sheets only — a photo has no place.
      let spot = null;
      if (src.kind === 'sheet') {
        const ring = marks.find(m => m.type === 'ring'); const arrow = marks.find(m => m.type === 'arrow');
        const at = ring ? { x: ring.x, y: ring.y } : arrow ? { x: arrow.x2, y: arrow.y2 } : { x: x0 + w / 2, y: y0 + h / 2 };
        spot = { sheet_id: src.id, x: Math.round(at.x), y: Math.round(at.y), w: img.naturalWidth, h: img.naturalHeight };
      }
      await onCapture(blob, name, src, spot);
      setCaptured(list => [...list, name]);
      setMarks([]);
      setFlash(`Attached ${name}`); setTimeout(() => setFlash(''), 2500);
    } catch (e) { setErr('Could not capture that view.'); }
    setBusy('');
  }

  const tool = (on, label, act) => (
    <button onClick={act} style={{ ...btn, padding: '5px 10px', fontSize: 12, background: on ? '#fff' : 'transparent', color: on ? NAVY : '#fff', borderColor: 'rgba(255,255,255,0.5)' }}>{label}</button>
  );
  const tabBtn = (key, label, n) => (
    <button onClick={() => setTab(key)} style={{ flex: 1, padding: '6px 4px', fontSize: 11.5, fontWeight: 800, border: 'none', borderBottom: `2px solid ${tab === key ? ORANGE : 'transparent'}`, background: 'none', color: tab === key ? NAVY : '#9ca3af', cursor: 'pointer' }}>
      {label}{n != null ? ` (${n})` : ''}
    </button>
  );

  // The marks, drawn over the image in image pixels (the wrapper is scaled with the image).
  const allMarks = pending ? [...marks, pending] : marks;
  const rImg = img ? ringPx() / view.scale : 0, lwImg = img ? strokePx() / view.scale : 0;
  const cursor = !img ? 'default' : mode === 'pan' ? 'grab' : 'crosshair';

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 6500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 1180, maxWidth: '100%', height: 'min(86vh, 820px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: NAVY, color: '#fff', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 800, background: ORANGE, borderRadius: 6, padding: '2px 8px' }}>Fieldwire</span>
          <span style={{ fontWeight: 800, fontSize: 14, flex: 1, minWidth: 120 }}>{title}{src ? ` · ${src.name}` : ''}</span>
          {captured.length > 0 && <span title={captured.join('\n')} style={{ fontSize: 12, fontWeight: 700, background: 'rgba(255,255,255,0.15)', borderRadius: 999, padding: '3px 10px' }}>✓ {captured.length} attached</span>}
          {img && tool(mode === 'pan', '✋ Pan / zoom', () => setMode('pan'))}
          {img && tool(mode === 'ring', '⭕ Ring', () => setMode('ring'))}
          {img && tool(mode === 'arrow', '➚ Arrow', () => setMode('arrow'))}
          {img && marks.length > 0 && tool(false, `Clear (${marks.length})`, () => setMarks([]))}
          {img && tool(false, 'Fit', fit)}
          {img && <button onClick={capture} disabled={!!busy} style={{ ...btn, background: ORANGE, color: '#fff', borderColor: ORANGE, fontSize: 12.5 }}>{busy === 'cap' ? 'Capturing…' : '📸 Capture this view'}</button>}
          <button onClick={onClose} style={{ ...btn, background: '#fff', color: NAVY, fontSize: 12.5 }}>{captured.length ? 'Done' : 'Close'}</button>
        </div>
        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <div style={{ width: 230, borderRight: '1px solid #eee', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
            <div style={{ display: 'flex', borderBottom: '1px solid #eee' }}>
              {tabBtn('sheets', 'Sheets', sheets?.length)}
              {tabBtn('photos', 'Photos', photos?.length)}
            </div>
            <div style={{ overflowY: 'auto', padding: 8, flex: 1 }}>
              {tab === 'sheets' && (<>
                {sheets === null && <div style={muted}>Loading…</div>}
                {sheets && sheets.length === 0 && <div style={{ ...muted, padding: 6 }}>This job has no Fieldwire sheets yet — link the project and Sync on the Fieldwire pillow.</div>}
                {(sheets || []).map(s => (
                  <button key={s.id} onClick={() => setSrc({ kind: 'sheet', id: s.id, name: s.name, url: `/projects/${projectId}/fieldwire/sheets/${s.id}/image` })}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '6px 8px', borderRadius: 8, border: '1px solid ' + (src?.id === s.id ? ORANGE : 'transparent'), background: src?.id === s.id ? '#fff7ed' : 'transparent', cursor: 'pointer', font: 'inherit' }}>
                    {s.thumb_url ? <img src={s.thumb_url} alt="" style={{ width: 44, height: 30, objectFit: 'cover', borderRadius: 4, border: '1px solid #e5e7eb', flexShrink: 0 }} loading="lazy" /> : <span style={{ width: 44, height: 30, flexShrink: 0 }} />}
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                  </button>
                ))}
              </>)}
              {tab === 'photos' && (<>
                {photos === null && <div style={muted}>Loading…</div>}
                {photos && photos.length === 0 && <div style={{ ...muted, padding: 6 }}>No photos on the job's Fieldwire pins yet.</div>}
                {(photos || []).map(p => (
                  <button key={p.id} onClick={() => setSrc({ kind: 'photo', id: p.id, name: p.pin_name ? `Pin ${p.pin} ${p.pin_name}` : `Pin ${p.pin || ''}`.trim(), url: `/projects/${projectId}/fieldwire/photos/${p.id}/image` })}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '6px 8px', borderRadius: 8, border: '1px solid ' + (src?.id === p.id ? ORANGE : 'transparent'), background: src?.id === p.id ? '#fff7ed' : 'transparent', cursor: 'pointer', font: 'inherit' }}>
                    {p.thumb_url ? <img src={p.thumb_url} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb', flexShrink: 0 }} loading="lazy" /> : <span style={{ width: 44, height: 44, flexShrink: 0 }} />}
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.pin_name || (p.pin ? `Pin #${p.pin}` : 'Photo')}</span>
                      <span style={{ display: 'block', fontSize: 10.5, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{[p.sheet_name, p.team_name, p.user_name, fmtWhen(p.captured_at || p.created_at)].filter(Boolean).join(' · ')}</span>
                    </span>
                  </button>
                ))}
              </>)}
            </div>
          </div>
          <div ref={boxRef} onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
            style={{ flex: 1, position: 'relative', overflow: 'hidden', background: '#f3f4f6', cursor, userSelect: 'none' }}>
            {!src && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontSize: 14, textAlign: 'center', padding: 20, lineHeight: 1.5 }}>Pick a sheet or a photo on the left.<br />Scroll to zoom, drag to move. ⭕ Ring: click the spot. ➚ Arrow: drag toward the spot. Then 📸 Capture — as many views as you need, then Done.</div>}
            {src && busy === 'img' && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>Loading {src.name}…</div>}
            {err && <div style={{ position: 'absolute', top: 10, left: 10, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, padding: '6px 10px', fontSize: 12.5, fontWeight: 700, zIndex: 2 }}>{err}</div>}
            {flash && <div style={{ position: 'absolute', top: 10, left: 10, background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 8, padding: '6px 10px', fontSize: 12.5, fontWeight: 700, zIndex: 2 }}>✓ {flash} — keep going or press Done</div>}
            {img && imgUrl && (
              <div style={{ position: 'absolute', left: 0, top: 0, transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})`, transformOrigin: '0 0', width: img.naturalWidth, height: img.naturalHeight }}>
                <img src={imgUrl} alt={src.name} draggable={false} style={{ display: 'block', width: img.naturalWidth, height: img.naturalHeight, pointerEvents: 'none' }} />
                <svg width={img.naturalWidth} height={img.naturalHeight} viewBox={`0 0 ${img.naturalWidth} ${img.naturalHeight}`} style={{ position: 'absolute', left: 0, top: 0, overflow: 'visible', pointerEvents: 'none' }}>
                  {allMarks.map((m, i) => m.type === 'ring'
                    ? <circle key={i} cx={m.x} cy={m.y} r={rImg} fill="none" stroke={RED} strokeWidth={lwImg} />
                    : (() => {
                      const ang = Math.atan2(m.y2 - m.y1, m.x2 - m.x1), head = Math.max(lwImg * 4, rImg * 0.9);
                      const sx = m.x2 - Math.cos(ang) * head * 0.7, sy = m.y2 - Math.sin(ang) * head * 0.7;
                      const pts = `${m.x2},${m.y2} ${m.x2 - head * Math.cos(ang - Math.PI / 7)},${m.y2 - head * Math.sin(ang - Math.PI / 7)} ${m.x2 - head * Math.cos(ang + Math.PI / 7)},${m.y2 - head * Math.sin(ang + Math.PI / 7)}`;
                      return <g key={i}><line x1={m.x1} y1={m.y1} x2={sx} y2={sy} stroke={RED} strokeWidth={lwImg} strokeLinecap="round" /><polygon points={pts} fill={RED} /></g>;
                    })())}
                </svg>
              </div>
            )}
            {img && <div style={{ position: 'absolute', right: 10, bottom: 10, background: 'rgba(255,255,255,0.9)', borderRadius: 6, padding: '3px 8px', fontSize: 11, color: '#6b7280', fontWeight: 700 }}>{Math.round(view.scale * 100)}%</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
